import React, { useMemo, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Activity } from 'lucide-react';
import { Modal, Button } from 'react-bootstrap';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import { Routes } from '#Routes';
import {
  useFetchBloodPressureReadingsQuery,
  FETCH_BLOOD_PRESSURE_READINGS_ARG,
  normalizeBloodPressureReadingsList,
} from '#store';
import {
  aggregateBloodPressureDailyMax,
  getLatestDailyBloodPressure,
  sortBloodPressureReadingsOldestToNewest,
} from '#utils/bloodPressureReadings';
import { CardShell } from './dashboardCardPrimitives';
import { BloodPressureReadingsChart } from './BloodPressureReadingsChart';
import { AddBloodPressureModal } from './AddBloodPressureModal';

/** Ciemna zieleń marki */
const SPARKLINE_STROKE = '#0D5C53';

const SPARKLINE_READING_COUNT = 7;

/**
 * Ścieżka SVG — oś Y = min/max z przekazanych wartości (ostatnie pomiary) + padding.
 */
function bloodPressureSparklinePathD(values) {
  if (!values.length) return '';
  const pts = values.length >= 2 ? [...values] : [values[0], values[0]];
  const n = pts.length;

  const dataMin = Math.min(...pts);
  const dataMax = Math.max(...pts);
  let span = dataMax - dataMin;
  const pad = span === 0 ? 5 : Math.max(2, span * 0.1);
  const yMin = dataMin - pad;
  const yMax = dataMax + pad;
  span = yMax - yMin;

  const vbW = 100;
  const vbH = 28;
  const padX = 2;
  const padY = 3;
  const innerW = vbW - 2 * padX;
  const innerH = vbH - 2 * padY;

  const parts = pts.map((v, i) => {
    const x = padX + (n === 1 ? innerW / 2 : (i / (n - 1)) * innerW);
    const t = (v - yMin) / span;
    const y = padY + innerH - t * innerH;
    return `${i === 0 ? 'M' : 'L'} ${x.toFixed(2)} ${y.toFixed(2)}`;
  });

  return parts.join(' ');
}

function BloodPressureSparklineSvg({ values }) {
  const d = useMemo(() => bloodPressureSparklinePathD(values), [values]);
  if (!d) return null;

  return (
    <svg
      className='bp-sparkline-svg d-block w-100'
      viewBox='0 0 100 28'
      preserveAspectRatio='none'
      aria-hidden
    >
      <path
        d={d}
        fill='none'
        stroke={SPARKLINE_STROKE}
        strokeWidth={2}
        strokeLinecap='round'
        strokeLinejoin='round'
        vectorEffect='non-scaling-stroke'
      />
    </svg>
  );
}

/**
 * Główny odczyt: ostatni dzień kalendarzowy — max(sys)/max(dia) przy wielu pomiarach tego dnia.
 * Sparkline: ostatnie 7 pojedynczych pomiarów (skurczowe), oś Y z min/max tych 7 wartości.
 */
export function BloodPressureCard() {
  const { t, i18n } = useTranslation();
  const [showChart, setShowChart] = useState(false);
  const [showAddReading, setShowAddReading] = useState(false);
  const {
    data: rawData,
    isLoading,
    isError,
  } = useFetchBloodPressureReadingsQuery(FETCH_BLOOD_PRESSURE_READINGS_ARG);

  const { readingLabel, sparkValues, hasAnyReading, timelineReadings } = useMemo(() => {
    const list = normalizeBloodPressureReadingsList(rawData);
    const sorted = sortBloodPressureReadingsOldestToNewest(list);
    const daily = aggregateBloodPressureDailyMax(sorted);
    const latest = getLatestDailyBloodPressure(daily);
    const readingLabelInner = latest ? `${latest.systolic}/${latest.diastolic}` : null;

    const withSys = sorted.filter((r) => Number.isFinite(Number(r?.systolic)));
    const lastN = withSys.slice(-SPARKLINE_READING_COUNT);
    const sparkValuesInner = lastN.map((r) => Number(r.systolic));
    return {
      readingLabel: readingLabelInner,
      sparkValues: sparkValuesInner,
      hasAnyReading: !!latest,
      timelineReadings: sorted,
    };
  }, [rawData]);

  const chartInteractive = hasAnyReading && !isLoading && !isError;
  /** Klik w kafelek przy braku pomiarów → modal dodania (jak w kalkulatorach). */
  const emptyInteractive = !hasAnyReading && !isLoading && !isError;
  const cardInteractive = chartInteractive || emptyInteractive;

  const handleCardActivate = useCallback(() => {
    if (chartInteractive) setShowChart(true);
    else if (emptyInteractive) setShowAddReading(true);
  }, [chartInteractive, emptyInteractive]);

  const onKeyDownCard = useCallback(
    (e) => {
      if (!cardInteractive) return;
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleCardActivate();
      }
    },
    [cardInteractive, handleCardActivate],
  );

  const unit = 'mmHg';
  const title = t('dashboard.blood-pressure-card-title');
  const ariaTrend = t('dashboard.blood-pressure-trend-aria');

  return (
    <>
      <CardShell
        className={cn(
          'bp-dashboard-card h-100 min-h-0 flex-1',
          cardInteractive && 'bp-dashboard-card--interactive',
        )}
        {...(cardInteractive
          ? {
              role: 'button',
              tabIndex: 0,
              onClick: handleCardActivate,
              onKeyDown: onKeyDownCard,
              'aria-label': chartInteractive
                ? t('dashboard.blood-pressure-open-chart-aria')
                : t('dashboard.blood-pressure-add-reading-aria'),
            }
          : {})}
      >
        <div className='shrink-0 border-bottom bg-white px-4 py-3 pb-2'>
          <div
            className='mb-0 d-flex align-items-center gap-2 fw-semibold text-dark'
            style={{ fontSize: '1rem' }}
          >
            <Activity
              className='flex-shrink-0'
              style={{ width: '1.25rem', height: '1.25rem', color: 'var(--primary)' }}
              strokeWidth={2}
              aria-hidden
            />
            {title}
          </div>
        </div>

        <div className='bp-dashboard-card__body'>
          {isLoading ? (
            <div className='d-flex align-items-center justify-content-center py-4 w-100'>
              <div className='spinner-border text-primary' role='status'>
                <span className='visually-hidden'>Loading</span>
              </div>
            </div>
          ) : isError ? (
            <p className='text-muted small text-center mb-0 px-2'>
              {t('dashboard.blood-pressure-load-error')}
            </p>
          ) : (
            <div className='flex flex-col items-center justify-center gap-1 text-center px-2'>
              {hasAnyReading ? (
                <div className='flex flex-wrap items-baseline justify-center gap-x-2 gap-y-1'>
                  <span className='bp-dashboard-reading-num text-foreground'>{readingLabel}</span>
                  <span className='bp-dashboard-reading-unit text-muted-foreground'>{unit}</span>
                </div>
              ) : (
                <>
                  <span className='text-muted small'>{t('dashboard.blood-pressure-no-readings')}</span>
                  <span className='text-muted' style={{ fontSize: '0.7rem' }}>
                    {t('dashboard.blood-pressure-empty-hint')}
                  </span>
                </>
              )}
            </div>
          )}
        </div>

        {!isLoading && !isError && (
          <div
            className='bp-dashboard-card__sparkline shrink-0 px-4 pb-3 pt-1'
            role={sparkValues.length > 0 ? 'img' : undefined}
            aria-label={sparkValues.length > 0 ? ariaTrend : undefined}
            aria-hidden={sparkValues.length === 0 ? true : undefined}
          >
            {sparkValues.length > 0 ? (
              <div className='bp-dashboard-sparkline-wrap mx-auto w-100'>
                <BloodPressureSparklineSvg values={sparkValues} />
              </div>
            ) : (
              <div
                className='bp-dashboard-sparkline-wrap mx-auto w-100'
                style={{ minHeight: '1.75rem' }}
              />
            )}
          </div>
        )}
      </CardShell>

      <Modal show={showChart} onHide={() => setShowChart(false)} size='lg' centered>
        <Modal.Header closeButton>
          <Modal.Title>{t('dashboard.blood-pressure-chart-modal-title')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <BloodPressureReadingsChart
            readingsChronological={timelineReadings}
            lang={i18n.language}
          />
        </Modal.Body>
        <Modal.Footer className='d-flex flex-wrap gap-2 align-items-center justify-content-between'>
          <div className='d-flex flex-wrap gap-2'>
            <Button variant='logo-green' onClick={() => setShowAddReading(true)}>
              {t('dashboard.blood-pressure-add-reading')}
            </Button>
            <Button as={Link} variant='outline-primary' to={Routes.HealthCalculators.path}>
              {t('dashboard.blood-pressure-go-to-calculators')}
            </Button>
          </div>
          <Button variant='secondary' onClick={() => setShowChart(false)}>
            {t('forms.close')}
          </Button>
        </Modal.Footer>
      </Modal>

      <AddBloodPressureModal show={showAddReading} onHide={() => setShowAddReading(false)} />
    </>
  );
}
